import { BehaviorSubject, Observable, Subscription, isObservable } from "rxjs";

function initProps<T extends object, BaseType extends object>(
  props: T,
  instance: BaseType,
  registerSubject?: (subject: BehaviorSubject<any>) => void,
) {
  const keys = Object.keys(props) as (keyof T & string)[];
  Object.defineProperties(
    instance,
    keys.reduce((defs: PropertyDescriptorMap, key) => {
      const subject = new BehaviorSubject(props[key]);
      defs[key] = {
        get() {
          return subject.value;
        },
        set(val: any) {
          if (!Object.is(val, subject.value)) subject.next(val);
        },
        enumerable: true,
        configurable: true,
      };
      defs[`${key}$`] = {
        value: subject,
        writable: false,
        enumerable: true,
        configurable: true,
      };
      registerSubject?.(subject);
      return defs;
    }, {}),
  );
  return keys;
}

export type ObservableProps<T extends object> = {
  readonly [key in `${keyof T & string}$`]: key extends `${infer Q extends keyof T & string}$`
    ? Observable<T[Q]>
    : never;
} & T;

export type ObservablePropsCtor<
  BaseArgs extends readonly unknown[] = [],
  BaseType = {},
> = {
  /**
   * @param initialProps The initial value of the observable props
   * @param destroySignal An optional {@link Subscription} or {@link Observable} which will trigger
   * the cleanup of the Observers.
   */
  new <T extends object>(
    initialProps: T,
    destroySignal?: Subscription | Observable<any> | null,
    ...baseArgs: BaseArgs
  ): BaseType & ObservableProps<T>;
};

function makeMixin(base?: null): ObservablePropsCtor;
function makeMixin<Base extends { new (...args: any): any }>(
  base: Base,
): ObservablePropsCtor<ConstructorParameters<Base>, InstanceType<Base>>;
function makeMixin<Base extends { new (...args: any): unknown }>(
  base?: Base | null,
):
  | ObservablePropsCtor<ConstructorParameters<Base>, InstanceType<Base>>
  | ObservablePropsCtor;
function makeMixin<Base extends { new (...args: any): unknown }>(
  base?: Base | null,
) {
  function constructInstance(newTarget: Function | undefined, args: any[]) {
    if (base != null) {
      return Reflect.construct(base, args, new.target);
    }
    if (newTarget != null) {
      return Object.create(newTarget.prototype);
    }
    return Object.create(base === null ? null : Object.prototype);
  }
  function ObservableProps<T extends object>(
    initialProps: T,
    destroySignal?: Subscription | Observable<any> | null,
    ...restArgs: ConstructorParameters<Base>
  ) {
    const self = constructInstance(new.target, restArgs);
    if (destroySignal instanceof Subscription) {
      initProps(initialProps, self, (subject) => destroySignal.add(subject));
    } else if (isObservable(destroySignal)) {
      initProps(initialProps, self, (subject) => {
        destroySignal.subscribe({
          complete() {
            subject.complete();
          },
          error(err) {
            subject.error(err);
          },
        });
      });
    } else initProps(initialProps, self);
    return self;
  }
  if (base != null) {
    Object.defineProperty(ObservableProps, "prototype", {
      value: base.prototype,
    });
  } else {
    Object.defineProperty(ObservableProps, "prototype", {
      value: base === null ? null : Object.prototype,
    });
  }
  return ObservableProps as unknown as ObservablePropsCtor<
    ConstructorParameters<Base>,
    InstanceType<Base>
  >;
}

const nullProto = Symbol("nullProto");
// (symbol & { description?: string })
// is a workaround for typescript not accepting "symbol" to
// satisfy constraint "object". But implementation wise, it works.
const cache = new WeakMap<
  Function | (symbol & { description?: string }),
  ObservablePropsCtor<any, any>
>();

function observablePropsMixin<Base extends { new (...args: any): unknown }>(
  base?: Base | null,
) {
  const key = base === null ? nullProto : base ?? Object;
  let ctor = cache.get(key);
  if (ctor != null) return ctor;
  ctor = makeMixin(base);
  cache.set(key, ctor);
  return ctor;
}

/**
 * Mixin that sets up props to be observable.
 *
 * `new ObservableProps()` is an alias for `new (ObservabeProps())()`
 * making it possible to extend ObservableProps directly.
 *
 * The Observers are only triggered when a value changes per {@link Object.is} logic.
 * So multiple assignments of the same value will not trigger multiple updates.
 *
 * The mixins do not affect the prototype chain, and only create properties on instanced objects.
 *
 * @example
 * Basic usage
 * ```ts
 * class Example extends ObservableProps<{
 *   a: number;
 *   b: string;
 * }> {
 *   constructor() {
 *     super({
 *       a: 0,
 *       b: 'test'
 *     });
 *   }
 *   nonObservableProp = 0;
 * }
 *
 * const example = new Example();
 * example.a$.subscribe(console.log.bind(console, 'a set to: %o'));
 * example.b$.subscribe(console.log.bind(console, 'b set to: %o'));
 *
 * example.a = 42;
 * // log - a set to: 42
 *
 * example.b = 'new test';
 * // log - b set to: 'new test'
 *
 * example.a = 42;
 * // no output - Object.is(42, 42) === true
 *
 *
 * example.nonObservableProp$.subscribe(console.log.bind(console, 'nonObservableProp set to: %o'));
 * // Error: nonObservableProp$ is undefined since it's not defined in the ObservableProps mixin
 * ```
 *
 * @example
 * With Superclass
 * ```
 * class SuperClass {
 *   constructor(superArgument: string) {
 *     console.log('superArgument is: %o', superArgument);
 *   }
 *   superProp = "This is a prop on the superclass";
 * }
 * class Example extends ObservableProps(SuperClass)<{
 *   a: number;
 *   b: string;
 * }> {
 *   constructor() {
 *     super(
 *       {
 *         a: 0,
 *         b: ''
 *       },
 *       null,
 *       'test'
 *     );
 *   }
 * }
 *
 * const example = new Example();
 * // log - superArgument is: 'test'
 *
 * example.a$.subscribe(console.log.bind(console, 'a set to: %o'));
 * example.b$.subscribe(console.log.bind(console, 'b set to: %o'));
 *
 * example.a = 42;
 * // log - a set to: 42
 *
 * example.b = 'test';
 * // log - b set to: 'test'
 *
 * console.log(example.superProp);
 * // log - This is a prop on the superclass
 *
 * example.superProp$.subscribe(console.log.bind(console, 'superProp set to: %o'));
 * // Error: superProp$ is undefined since it's defined on the superClass, not the mixin
 * ```
 */
export const ObservableProps = function ObservableProps<
  Base extends { new (...args: any): unknown },
>(
  ...args:
    | [base?: Base | null]
    | [
        initialProps: Record<string, any>,
        destroySignal?: Subscription | Observable<any> | null,
      ]
) {
  if (new.target != null) {
    return Reflect.construct(observablePropsMixin(), args, new.target);
  }
  return observablePropsMixin(
    ...(args as Exclude<typeof args, readonly [object, ...any[]]>),
  );
} as {
  /**
   * @param initialProps The initial value of the observable props
   * @param destroySignal An optional {@link Subscription} or {@link Observable} which will trigger
   * the cleanup of the Observers.
   */
  new <T extends object>(
    initialProps: T,
    destroySignal?: Subscription | Observable<any> | null,
  ): ObservableProps<T>;
  /**
   * @param base The base class to extend
   */
  (base?: null): ObservablePropsCtor;
  /**
   * @param base The base class to extend
   */
  <Base extends { new (...args: any): unknown }>(
    base: Base,
  ): ObservablePropsCtor<ConstructorParameters<Base>, InstanceType<Base>>;
};
Object.defineProperty(ObservableProps, "prototype", {
  value: Object.prototype,
});
